import { IAppSetting } from '@addins/core/core';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { AppSettingProviderService, SagaSettingsService } from '@services/settings';
import { Subscription } from 'rxjs';
import { ListSettingValue } from '../../schema/list-setting-value';
import { SettingValue } from '../../schema/setting-value';
import { ListSettingWithNames } from '../../views/settings-editor/settings-editor.component';

@Component({
  selector: 'app-location-settings',
  templateUrl: './location-settings.component.html',
  styleUrls: ['./location-settings.component.scss']
})
export class LocationSettingsComponent implements OnInit, OnDestroy {
  readOnly: boolean = false;

  private _locationTrackingEnabled: IAppSetting<boolean> = null;
  get locationTrackingEnabled(): boolean {
    return this._locationTrackingEnabled && this._locationTrackingEnabled.current;
  }

  private distanceFilter: IAppSetting<number> = null;
  private distanceFilterSub: Subscription;

  private _distanceFilterSettings: ListSettingWithNames;
  get distanceFilterSettings(): ListSettingWithNames {
    return this._distanceFilterSettings;
  }

  private updateInterval: IAppSetting<number> = null;
  private updateIntervalSub: Subscription;

  private _updateIntervalSettings: ListSettingWithNames;
  get updateIntervalSettings(): ListSettingWithNames {
    return this._updateIntervalSettings;
  }

  constructor(private appSettingProvider: AppSettingProviderService, private sagaSettings: SagaSettingsService) {}

  ngOnInit() {
    this.readOnly = this.sagaSettings.getValue('SagaMobileWebClient.ReadOnlyLocationTrackingSettings');
    this.appSettingProvider.getLocationTracking().subscribe(appSetting => (this._locationTrackingEnabled = appSetting));

    this.appSettingProvider
      .getLocationTrackingDistanceFilter()
      .subscribe(appSetting => this.onLocationTrackingDistanceFilterReady(appSetting));

    this.appSettingProvider
      .getLocationTrackingUpdateInterval()
      .subscribe(appSetting => this.onLocationTrackingUpdateIntervalReady(appSetting));
  }

  ngOnDestroy() {
    this.distanceFilterSub?.unsubscribe();
    this.updateIntervalSub?.unsubscribe();
  }

  onLocationTrackingChange($event) {
    this._locationTrackingEnabled.setValue($event.detail.checked);
  }

  private onLocationTrackingDistanceFilterReady(appSetting: IAppSetting<number>) {
    this.distanceFilter = appSetting;
    this._distanceFilterSettings = this.createDistanceFilterSettings(appSetting.current);

    this.distanceFilterSub = this._distanceFilterSettings.setting.$change.subscribe((settingValue: SettingValue) =>
      this.onDistanceFilterChanged(settingValue.value)
    );
  }

  private createDistanceFilterSettings(currentValue: number): ListSettingWithNames {
    const settingValues: SettingValue[] = this.readNumberArraySetting('SagaMobileWebClient.LocationTrackingDistanceFilters').map(
      i => new SettingValue(`${i}`, i)
    );
    const listSettingValue: ListSettingValue = new ListSettingValue('Mobile.LocationTrackingDistanceFilter', settingValues);
    let currentSettingValue: SettingValue = settingValues.filter(settingValue => settingValue.value === currentValue)[0];
    listSettingValue.select(currentSettingValue);

    return {
      setting: listSettingValue,
      displayNames: {},
      readonly: this.readOnly
    };
  }

  private onDistanceFilterChanged(value: number) {
    this.distanceFilter.setValue(value);
  }

  private readNumberArraySetting(settingName: string): number[] {
    return this.sagaSettings
      .getValue(settingName)
      .split(',')
      .map(i => parseInt(i, 10));
  }

  private onLocationTrackingUpdateIntervalReady(appSetting: IAppSetting<number>) {
    this.updateInterval = appSetting;
    this._updateIntervalSettings = this.createUpdateIntervalSettings(appSetting.current);

    this.updateIntervalSub = this._updateIntervalSettings.setting.$change.subscribe((settingValue: SettingValue) =>
      this.onUpdateIntervalChanged(settingValue.value)
    );
  }

  private createUpdateIntervalSettings(currentValue: number): ListSettingWithNames {
    const settingValues: SettingValue[] = this.readNumberArraySetting('SagaMobileWebClient.LocationTrackingUpdateIntervals').map(
      i => new SettingValue(`${i}`, i)
    );
    const listSettingValue: ListSettingValue = new ListSettingValue('Mobile.LocationTrackingUpdateInterval', settingValues);
    let currentSettingValue: SettingValue = settingValues.filter(settingValue => settingValue.value === currentValue)[0];
    listSettingValue.select(currentSettingValue);

    return {
      setting: listSettingValue,
      displayNames: {},
      readonly: this.readOnly
    };
  }

  private onUpdateIntervalChanged(value: number) {
    this.updateInterval.setValue(value);
  }
}
